import { format, utcToZonedTime } from 'date-fns-tz';

/**
 * Parses a legacy date array format from the API.
 * Format: [year, month, day] where month is 1-indexed (1 = January, 12 = December)
 * 
 * @deprecated This is a legacy format that should be removed once the API is updated
 * to consistently return ISO 8601 date strings. This function should be revisited
 * and removed when the backend migration is complete.
 */
export const parseLegacyDateArray = (dateArray: number[]): Date | null => {
    if (dateArray.length < 3) {
        return null;
    }

    try {
        const [year, month, day] = dateArray;
        const date = new Date(year, month - 1, day);
        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
};

/**
 * Safely parses a date string and returns a Date object or null if invalid.
 * Useful for converting API date strings to Date objects for date pickers.
 */
export const safeParseDate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) {
        return null;
    }

    try {
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
};

/**
 * Safely gets a timestamp from a date string, returning null if invalid.
 * Useful for sorting and comparing dates.
 */
export const safeGetTimestamp = (dateStr: string | null | undefined): number | null => {
    if (!dateStr) {
        return null;
    }

    try {
        const timestamp = new Date(dateStr).getTime();
        return isNaN(timestamp) ? null : timestamp;
    } catch {
        return null;
    }
};

/**
 * Creates a comparator function for sorting objects by a date string property.
 * @param getDateStr Function to extract the date string from the object
 * @param ascending If true, sorts ascending (oldest first), otherwise descending (newest first)
 */
export const createDateComparator = <T>(
    getDateStr: (item: T) => string | null | undefined,
    ascending: boolean = true
): (a: T, b: T) => number => {
    return (a: T, b: T) => {
        const aTs = safeGetTimestamp(getDateStr(a));
        const bTs = safeGetTimestamp(getDateStr(b));

        if (aTs === null || bTs === null) {
            return 0;
        }

        return ascending ? aTs - bTs : bTs - aTs;
    };
};

export const parseDateTime = (dateTimeStr: string | null | undefined, timeZone?: string): { date: string, time: string } => {
    if (!dateTimeStr) {
        return { date: '-', time: '-' };
    }

    try {
        const testDate = new Date(dateTimeStr);
        if (isNaN(testDate.getTime())) {
            return { date: '-', time: '-' };
        }

        let effectiveTimeZone: string;
        if (timeZone) {
            effectiveTimeZone = timeZone;
        } else {
            try {
                const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
                effectiveTimeZone = resolvedOptions.timeZone || 'UTC';
            } catch (tzError) {
                effectiveTimeZone = 'UTC';
            }
        }

        if (!effectiveTimeZone || effectiveTimeZone.trim() === '') {
            effectiveTimeZone = 'UTC';
        }

        const zonedDate = utcToZonedTime(dateTimeStr, effectiveTimeZone);

        if (isNaN(zonedDate.getTime())) {
            return { date: '-', time: '-' };
        }

        const date = format(zonedDate, 'yyyy-MM-dd', { timeZone: effectiveTimeZone });
        const time = format(zonedDate, 'HH:mm', { timeZone: effectiveTimeZone });

        return { date, time };
    } catch (error) {
        console.warn('Error parsing date:', dateTimeStr, error);
        return { date: '-', time: '-' };
    }
}
